import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PencilIcon } from '@heroicons/react/outline';
import { Form, Formik, useFormikContext } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from 'urql';
import { AttributeView } from '~/components/AttributeView';
import { FilesF } from '~/components/FileLink';
import { PromptEffect } from '~/components/Prompt';
import { TagEntityType } from '~/components/TagCombobox';
import { useTags } from '~/components/TagCombobox/tags';
import { AssetForm } from '~/components/assets/AssetForm';
import { AssetView } from '~/components/assets/AssetView';
import { DeploymentForm } from '~/components/assets/DeploymentForm';
import { AttributeSelect_Query } from '~/components/form/AttributeSelect';
import { Button } from '~/components/form/SubmitButton';
import { ReadOnlyEditor } from '~/components/form/editor/ReadOnlyEditor';
import { CardSmall } from '~/components/ui/CardSmall';
import { HelperPopup } from '~/components/ui/HelperPopup';
import { PopupDialogNew } from '~/components/ui/PopupDialogNew';
import { ReadValue } from '~/components/ui/ReadValue';
import { StatusBadge } from '~/components/ui/StatusBadge';
import { AttachmentFields } from '~/components/upload/AttachmentFields';
import { toAttachmentInput } from '~/components/upload2';
import { UploadField, UploadResult } from '~/components/upload2/Upload';
import {
  ChecklistItem,
  IsCurrentUserAdminDocument,
  JobStatus,
  useCreateDeploymentMutation,
} from '~/generated/graphql';
import { FragmentType, getFragmentData, graphql } from '~/gql';
import { AttributeSelectDocument, JobQuery, TaskQuery } from '~/gql/graphql';
import { extractProperty } from '~/helpers/collection';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useTaskAssets } from '~/hooks/useTaskAssets';
import { SideLayout } from '~/layouts/side/SideLayout';
import { fadeVariants } from '~/utils/animations';
import { TextAreaField } from '../../form/textarea/TextArea';
import { StartJobButton } from '../JobView';
import { MemoGallery } from '../JobView/Feed/Gallery';
import {
  CollectAttributes,
  getAttributeFormValues,
} from '../JobView/JobReadView/CollectAttributes';
import {
  ResetTaskStatusButton,
  // PauseTaskButton,
  // QuitTaskButton,
  // ResumeTaskButton,
  // StartTaskButton,
  WorkflowButtons,
} from '../WorkflowButtons';
import { ReadOnlyTransferTask } from './TransferTask/ReadOnlyTransferTask';
import { TransferTaskField } from './TransferTask/TransferTaskField';
import { UpdateTaskTags, UpdateTaskTagsField } from './UpdateTaskTags';

type Props = {
  enabled: boolean;
  job: NonNullable<JobQuery['job']>;
  task: TaskQuery['task'];
  onClose: () => void;
  onSuccess: () => void;
  template?: boolean;
  isRulesBased?: boolean;
};

type TaskFormData = {
  date: null;
  tags: string[];
  notes: string;
  attachments: UploadResult[];
  transfer: {
    selected: never[];
  };
};

enum ShowAssetAction {
  VIEW = 'view',
  EDIT = 'edit',
  CREATE = 'create',
  DEPLOY = 'deploy',
}
const { VIEW, EDIT, CREATE, DEPLOY } = ShowAssetAction;
type AssetTask = TaskQuery['task']['assets'][number];
type ShowAsset = AssetTask & {
  action: `${ShowAssetAction}`;
};

export const AddTagsToTaggableDocument = graphql(`
  mutation AddTagsToTaggable($input: AddTagsToTaggableInput!) {
    addTagsToTaggable(input: $input) {
      __typename
      ... on Task {
        id
      }
      tags {
        nodes {
          id
          entityType
          name
          category
          description
          colour
        }
      }
    }
  }
`);

export const RemoveTagsFromTaggableDocument = graphql(`
  mutation RemoveTagsFromTaggable($input: RemoveTagsFromTaggableInput!) {
    removeTagsFromTaggable(input: $input) {
      __typename
      ... on Task {
        id
      }
      tags {
        nodes {
          id
          entityType
          name
          category
          description
          colour
        }
      }
    }
  }
`);

export const CompleteCustomTaskDocument = graphql(`
  mutation CompleteCustomTask($input: CompleteTaskInput!) {
    completeCustomTask(input: $input) {
      id
      status
      updatedAt
      # Completing the task may also complete the job so also fetch job status
      # incase it changed
      job {
        id
        status
      }
    }
  }
`);

export const Custom = (props: Props) => {
  const { job, task } = props;
  const [, completeTask] = useMutation(CompleteCustomTaskDocument);

  const savedJobTasks = localStorage.getItem(`job.tasks_${job.id}`)
    ? JSON.parse(localStorage.getItem(`job.tasks_${job.id}`) || '')
    : null;

  const savedTaskForm: {
    taskId: string;
    notes: string;
    attachments: UploadResult[];
  } | null =
    savedJobTasks && savedJobTasks[`task.form_${task.id}`]
      ? savedJobTasks[`task.form_${task.id}`]
      : null;

  const initialValues = {
    date: null,
    tags: extractProperty(task.tags?.nodes, 'id'),
    notes: savedTaskForm?.notes ?? task?.notes ?? '',
    // images: savedTaskForm?.images || ([] as string[]),
    attachments:
      savedTaskForm?.attachments ??
      task.attachments?.map((attachment) => {
        const data = getFragmentData(
          AttachmentFields,
          attachment as FragmentType<typeof AttachmentFields>
        );
        return {
          id: data.id,
          kind: data.kind as 'image' | 'file',
          name: data.originalFilename,
          preview: data.thumb,
        };
      }) ??
      [],
    transfer: {
      selected: [],
    },
  };

  const handleTaskFormSubmit = async (values: TaskFormData) => {
    // Find any checklist items that need saving
    const checklist: ChecklistItem[] = [];
    document
      .querySelectorAll<HTMLInputElement>('.js-custom-task-checklist-item')
      .forEach((el) => {
        const key = el.getAttribute('name');
        if (key) checklist.push({ key, checked: el.checked === true });
      });

    const input = {
      id: task.id,
      date: values.date || new Date(), // Ensure date is never null
      tagIds: values.tags || [], // Ensure tagIds is always an array
      notes: values.notes,
      attachments: values.attachments.map(toAttachmentInput),
      checklist,
      transfer: values.transfer,
      attributes: await getAttributeFormValues('Task', task.id),
    };

    const res = await completeTask({ input });

    if (res.error) {
      toast.error(res.error.message.replace('[GraphQL] ', ''));
      return;
    }

    props.onSuccess();
  };

  // FIXME: makes little sense...
  if (job.name === 'Template' && job.jobNumber === 'J0000') {
    return (
      <AnimatePresence mode='wait'>
        <motion.div
          key={`template-${task.id}`}
          initial={fadeVariants.hidden}
          animate={fadeVariants.visible}
          exit={fadeVariants.exit}
          className='h-full w-full'
        >
          <TaskView template {...props} />
        </motion.div>
      </AnimatePresence>
    );
  }

  if (
    job.status !== JobStatus.InProgress ||
    task.status === JobStatus.Complete
  ) {
    return <TaskView {...props} />;
  }

  return (
    <Formik
      key={task.id}
      initialValues={initialValues}
      onSubmit={handleTaskFormSubmit}
    >
      <TaskForm {...props} />
    </Formik>
  );
};

/**
 * Read only task view
 */
const TaskView = ({
  job,
  task,
  onClose,
  template = false,
  isRulesBased = false,
}: Props) => {
  const { t } = useTranslation(['translation', 'job']);
  const description = ReadOnlyEditor({ content: task.description });
  const [result] = useQuery({
    query: AttributeSelect_Query,
    variables: { entityType: 'Site' },
  });
  const attributesList = result?.data?.attributes;

  // TODO would prefer this was passed down in props or context rather than fetched here
  const [result2] = useQuery({
    query: AttributeSelectDocument,
    variables: { entityType: 'Job' },
    requestPolicy: 'cache-first',
  });
  const allJobAttributes = result2.data?.attributes;

  // Query session to check if current user is an administrator
  // This is more efficient than fetching all users via ListUsers
  const [{ data: adminData }] = useQuery({
    query: IsCurrentUserAdminDocument,
    requestPolicy: 'cache-first',
  });

  // Check if current user has admin role
  const isCurrentUserAdmin =
    adminData?.session?.user?.roles?.some(
      (role: { key: string }) => role.key === 'admin'
    ) ?? false;

  // Check if user has edit permissions - if so, they're "internal" and can see configuration
  // First check job.permissions (if job exists), then fall back to checking if current user is an administrator
  // This allows the sharing panel to appear for administrators even when job is undefined
  // (e.g., when viewing job templates)
  const hasJobEditPermission = Array.isArray(job?.permissions)
    ? job.permissions.includes('edit')
    : false;
  const shouldShowPermissions = hasJobEditPermission || isCurrentUserAdmin;

  const needToStartJob = job.status === JobStatus.Accepted;
  const [shakeButton, setShakeButton] = useState<string>('');
  // State for viewing asset details before starting the job
  const [showAsset, setShowAsset] = useState<ShowAsset | null>(null);

  const [, addTags] = useMutation(AddTagsToTaggableDocument);
  const [, removeTags] = useMutation(RemoveTagsFromTaggableDocument);
  const [tagOptions] = useTags(TagEntityType.Task);

  // Use ref to track timeout for cleanup
  const shakeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  function shakeStartButton() {
    setShakeButton('wiggle-btn');

    // Clear any existing timeout
    if (shakeTimeoutRef.current) {
      clearTimeout(shakeTimeoutRef.current);
    }

    shakeTimeoutRef.current = setTimeout(() => {
      setShakeButton('');
      shakeTimeoutRef.current = null;
    }, 1000);
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only cleanup on unmount

  const taskAssets = useTaskAssets(task);

  const showMandatoryTaskHelper =
    task.required &&
    task.status !== JobStatus.Complete &&
    task.status !== JobStatus.Incomplete;

  // We have to memoize images to prevent a re-render closing the lightgallery modal
  const images = useMemo(() => {
    return (
      task.attachments
        ?.map((attachment) =>
          getFragmentData(
            AttachmentFields,
            attachment as FragmentType<typeof AttachmentFields>
          )
        )
        .filter(({ kind }) => kind === 'image') ?? []
    );
  }, [task.attachments]);

  const closeAssetPopup = () => {
    setShowAsset(null);
  };

  // Extract sharing permissions from task config for external users ONLY
  // Internal users (owners/admins) always have full access - no restrictions
  // External users see restricted content based on task config
  const sharingPermissions = useMemo(() => {
    // If user is internal (owner/admin), don't apply any restrictions
    if (shouldShowPermissions) return undefined;

    // If no config, don't apply restrictions (default to full access)
    if (!task.config) return undefined;

    // Apply sharing restrictions for external users
    const permissions = {
      shareAttributes: task.config.shareAttributes,
      shareJobHistory: task.config.shareJobHistory,
      shareDeploymentHistory: task.config.shareDeploymentHistory,
    };

    return permissions;
  }, [task.config, shouldShowPermissions]);

  // Get asset types for display (for rules-based templates with Asset tasks)
  const assetTypesDisplay = useMemo(() => {
    if (
      isRulesBased &&
      task.type === 'Asset' &&
      task.config?.assetTypes &&
      task.config.assetTypes.length > 0
    ) {
      const maxLength = 30; // Maximum characters to display
      const joined = task.config.assetTypes.join(', ');
      if (joined.length <= maxLength) {
        return joined;
      }
      // Truncate and add ellipsis
      return joined.substring(0, maxLength - 3) + '...';
    }
    return null;
  }, [isRulesBased, task.type, task.config?.assetTypes]);

  return (
    <>
      {/* Popup for viewing asset details before starting the job */}
      <PopupDialogNew
        isOpen={!!showAsset?.action}
        onClose={closeAssetPopup}
        title={showAsset?.name}
      >
        {showAsset?.action === VIEW && showAsset.id && (
          <AssetView
            isPopup={true}
            id={showAsset.id}
            jobId={task?.job?.id ?? job.id}
            taskId={task.id}
            sharingPermissions={sharingPermissions}
          />
        )}
      </PopupDialogNew>

      <SideLayout>
        <SideLayout.Head iconClass='arrow-left' onClose={onClose}>
          <div className='flex items-center gap-2'>
            <span>{task.name}</span>
            {assetTypesDisplay && (
              <span className='truncate text-sm text-grey-40'>
                ({assetTypesDisplay})
              </span>
            )}
          </div>
        </SideLayout.Head>
        <SideLayout.Body onClick={() => shakeStartButton()}>
          {task.status !== JobStatus.Created && (
            <StatusBadge prefix={t('job:task')} value={task.status} />
          )}

          {showMandatoryTaskHelper && (
            <MandatoryTaskHelper jobName={job.name} />
          )}

          {description && (
            <>
              <div className='label mb-1.5'>{t('description')}</div>
              <div className='relative'>
                {/* Add overlay so the checkboxes are not clickable  */}
                <div className='absolute inset-0 z-20 bg-transparent'></div>
                <div className='mb-6'>{description}</div>
              </div>
            </>
          )}

          {task.status === JobStatus.Complete &&
            task.jobAttributeIds &&
            task.jobAttributeIds.length && (
              <CollectAttributes
                entityType='Task'
                entityId={task.id}
                selections={task.jobAttributeIds}
                values={task.taskAttributes}
                readMode
              />
            )}

          <UpdateTaskTags
            options={tagOptions}
            value={task.tags?.nodes}
            onChange={async (tags) => {
              try {
                const currentTags = extractProperty(task.tags?.nodes, 'id');
                const toAdd = tags.filter((id) => !currentTags.includes(id));
                const toRemove = currentTags.filter((id) => !tags.includes(id));
                if (toAdd.length) {
                  const result = await addTags({
                    input: { taggableId: task.id, tagIds: toAdd },
                  });
                  if (result.error) {
                    toast.error(
                      result.error.message.replace('[GraphQL] ', '') ||
                        'Failed to add tags'
                    );
                    return;
                  }
                }
                if (toRemove.length) {
                  const result = await removeTags({
                    input: { taggableId: task.id, tagIds: toRemove },
                  });
                  if (result.error) {
                    toast.error(
                      result.error.message.replace('[GraphQL] ', '') ||
                        'Failed to remove tags'
                    );
                    return;
                  }
                }
              } catch (error) {
                console.error('Error updating tags:', error);
                toast.error('Failed to update tags');
              }
            }}
          />

          {task.type === 'Asset' && (
            <>
              <ReadValue
                label={
                  isRulesBased ? 'Will create a task for any' : 'Selection'
                }
              >
                {task.config?.selection === 'all'
                  ? isRulesBased
                    ? task.config?.assetTypes === undefined
                      ? 'It will apply to any asset with an asset type not included in other template task rules'
                      : 'Asset matching'
                    : 'All Matching Assets'
                  : 'Specific Assets'}
              </ReadValue>
              {task.config?.spaces && task.config.spaces.length > 0 && (
                <ReadValue
                  label={t('space', { count: task.config?.spaces?.length })}
                >
                  {task.config.spaces.join(', ')}
                </ReadValue>
              )}
              {/* Check if this is the default rule (assetTypes must be undefined, not empty array) */}
              {isRulesBased &&
              task.config?.selection === 'all' &&
              task.config?.assetTypes === undefined ? (
                <div className='border-blue-200 bg-blue-50 rounded-lg border p-4'>
                  <div className='mb-2 flex items-start gap-2'>
                    <div className='mt-0.5'>
                      <svg
                        className='text-blue-600 h-5 w-5'
                        fill='currentColor'
                        viewBox='0 0 20 20'
                      >
                        <path
                          fillRule='evenodd'
                          d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                    <div className='flex-1'>
                      <h4 className='text-blue-900 font-medium'>
                        Default Task Rule
                      </h4>
                      <p className='text-blue-800 mt-1 text-sm'>
                        This task will automatically be used for any asset with
                        a type that doesn&apos;t match the types specified in
                        your other task rules. This ensures all assets are
                        covered even when you add new asset types in the future.
                      </p>
                      <p className='text-blue-900 mt-2 text-sm font-medium'>
                        Targets: All asset types not covered by other rules
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                task.config?.assetTypes &&
                task.config.assetTypes.length > 0 && (
                  <ReadValue label={t('assetType')}>
                    {task.config.assetTypes.join(', ')}
                  </ReadValue>
                )
              )}
              {/* <ReadValue label='Allow assignee to'>
              {task.config?.allow?.map(upperCaseFirst).join(', ')}
            </ReadValue> */}
              <hr />
            </>
          )}

          {task.type === 'Stocktake' ? (
            <>
              {task.stocktake?.include && (
                <ReadValue label='Include' className='whitespace-pre-line'>
                  {task.stocktake.include
                    .map((type: string) => t(`itemType.${type}_plural`))
                    .join(', ')}
                </ReadValue>
              )}
              {task.stocktake?.itemStatus && (
                <ReadValue label='Item Status' className='whitespace-pre-line'>
                  {task.stocktake.itemStatus.join(', ')}
                </ReadValue>
              )}
              {task.stocktake?.spaces && (
                <ReadValue
                  label={t('space_plural')}
                  className='whitespace-pre-line'
                >
                  {task.stocktake.spaces.join(', ')}
                </ReadValue>
              )}
            </>
          ) : null}

          {task.attributes && task.attributes.length > 0 && (
            <ReadValue label='Attributes to Display'>
              {task.attributes.map((attr: any, idx: number) => (
                <p key={idx}>
                  {(attr.category ? `${attr.category} \\ ` : '') + attr.name}
                </p>
              ))}
            </ReadValue>
          )}

          {task.status !== JobStatus.Complete &&
            task.jobAttributeIds &&
            task.jobAttributeIds.length > 0 && (
              <>
                <div className='label text-medium mb-1 pt-1 text-grey-40'>
                  Custom Fields
                </div>
                {task.jobAttributeIds.map((attribute: string) => {
                  const found = allJobAttributes?.find(
                    ({ id }) => id === attribute
                  );
                  if (!found) {
                    return null;
                  }
                  return (
                    <p className='indent-5 text-sm' key={found.id}>
                      {(found.category ? `${found.category} \\ ` : '') +
                        found.name}
                    </p>
                  );
                })}
                <br />
              </>
            )}

          {template ? (
            <ReadValue label='Attributes to Audit'>
              {attributesList && attributesList.length
                ? attributesList
                    .filter((attr: any) =>
                      // @ts-ignore TOFIX
                      task.attribute?.attributes.includes(attr.id)
                    )
                    .map((attr: any, idx: number) => (
                      <p key={idx}>
                        {(attr.category ? `${attr.category} \\ ` : '') +
                          attr.name}
                      </p>
                    ))
                : null}
            </ReadValue>
          ) : (
            task.attributes &&
            task.attributes.length > 0 && (
              <AttributeView attributes={task.attributes} />
            )
          )}

          {taskAssets.map((asset) => {
            const isSeparator = typeof asset === 'string';

            if (isSeparator) {
              if (asset.startsWith('location:')) {
                const locationName = asset.replace('location:', '');
                return (
                  <div
                    key={asset}
                    className='mt-2 px-4 text-base font-medium text-primary'
                  >
                    <FontAwesomeIcon
                      className='mr-2 text-secondary'
                      icon={faLocationDot as IconProp}
                    />
                    {locationName}
                  </div>
                );
              } else if (asset.startsWith('space:')) {
                const spaceName = asset.replace('space:', '');
                return (
                  <div
                    key={asset}
                    className='mx-3 my-2 pl-1 text-sm text-secondary'
                  >
                    {spaceName}
                  </div>
                );
              }
              return null;
            }

            return (
              <div
                key={asset.id}
                className='cursor-pointer'
                onClick={() =>
                  setShowAsset({
                    ...asset,
                    action: VIEW,
                  })
                }
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowAsset({
                      ...asset,
                      action: VIEW,
                    });
                  }
                }}
                aria-label={`View asset ${asset.name}`}
              >
                <CardSmall
                  linkTo=''
                  titleText={asset.name}
                  secondaryText={asset.type}
                  showImage
                  image={asset.deployment?.image}
                />
              </div>
            );
          })}

          {/* Asset Sharing Permissions Section - shown after selected assets */}
          {task.type === 'Asset' && task.config && shouldShowPermissions && (
            <>
              <hr />
              <div className='label mb-1.5'>Share with External Assignee</div>
              <div className='mb-4 text-sm text-secondary'>
                If assigned to someone outside your organisation, they can view:
              </div>
              <div className='mb-4 flex flex-col gap-2'>
                {task.config.shareAttributes && (
                  <div className='flex items-center gap-2 text-sm'>
                    <span className='text-emerald-600'>✓</span>
                    <span>Attributes</span>
                  </div>
                )}
                {task.config.shareJobHistory && (
                  <div className='flex items-center gap-2 text-sm'>
                    <span className='text-emerald-600'>✓</span>
                    <span>Job History</span>
                  </div>
                )}
                {task.config.shareDeploymentHistory && (
                  <div className='flex items-center gap-2 text-sm'>
                    <span className='text-emerald-600'>✓</span>
                    <span>Deployment History</span>
                  </div>
                )}
                {!task.config.shareAttributes &&
                  !task.config.shareJobHistory &&
                  !task.config.shareDeploymentHistory && (
                    <div className='text-sm text-tertiary'>
                      No asset information is shared with external assignees
                    </div>
                  )}
              </div>
            </>
          )}

          <ReadValue label={t('notes')} className='whitespace-pre-line'>
            {task.notes}
          </ReadValue>

          <FilesF
            attachments={
              (task.attachments ?? []) as FragmentType<
                typeof AttachmentFields
              >[]
            }
          />
          <MemoGallery attachments={images} />

          {task.type === 'Transfer Stock' && job.location && task.transfer && (
            <ReadOnlyTransferTask task={task} />
            // <div className='-m-4'>
            //   <Stock location={job.location.__typename + ':' + job.location.id} />
            // </div>
          )}
        </SideLayout.Body>
        {!template && (
          <SideLayout.Foot className='p-4'>
            {needToStartJob && (
              <div className={shakeButton}>
                <StartJobButton id={job.id} />
              </div>
            )}
            <ResetTaskStatusButton id={task.id} jobStatus={job.status} />
          </SideLayout.Foot>
        )}
      </SideLayout>
    </>
  );
};
/**
 * This is the task form for DOING the task (perform task form)
 * This IS NOT the form for creating/editing the task
 */
const TaskForm = ({ enabled, job, task, onClose }: Props) => {
  const [showAsset, setShowAsset] = useState<ShowAsset | null>(null);
  const { values } = useFormikContext<TaskFormData>();
  const { t } = useTranslation(['translation', 'job', 'asset']);
  const [uploading, setUploading] = useState(false);

  // Query session to check if current user is an administrator
  // This is more efficient than fetching all users via ListUsers
  const [{ data: adminData }] = useQuery({
    query: IsCurrentUserAdminDocument,
    requestPolicy: 'cache-first',
  });

  // Check if current user has admin role
  const isCurrentUserAdmin =
    adminData?.session?.user?.roles?.some(
      (role: { key: string }) => role.key === 'admin'
    ) ?? false;

  // Check if user has edit permissions - if so, they're "internal" and can edit assets
  // First check job.permissions (if job exists), then fall back to checking if current user is an administrator
  const canEditAssets =
    Array.isArray(job.permissions) && job.permissions.includes('edit');
  const shouldShowPermissions = canEditAssets || isCurrentUserAdmin;

  // Determine if deploy button should be shown based on selection method
  const shouldShowDeployButton = (asset: any) => {
    // If assets were specifically selected (by ID), allow redeployment
    if (task.config?.selection === 'specific') {
      return true;
    }

    // If assets were selected by criteria ('all'), hide deploy button
    // because redeploying might remove them from the task
    if (task.config?.selection === 'all') {
      return false;
    }

    // Default: show deploy button
    return true;
  };

  const isEnabled =
    enabled && [JobStatus.Created, JobStatus.InProgress].includes(task.status);

  const description = ReadOnlyEditor({ content: task.description });
  const [, create] = useCreateDeploymentMutation();

  /**
   * This is to remember the user's inputs without completing the job
   */
  const { notes, attachments } = values;
  const taskFormValuesToSave = {
    taskId: task.id,
    notes,
    attachments,
  };
  if (notes || attachments.length > 0) {
    const savedJobTasks = localStorage.getItem(`job.tasks_${job.id}`)
      ? JSON.parse(localStorage.getItem(`job.tasks_${job.id}`) || '')
      : null;

    // TODO re-enable this feature after working out how it's going to work properly with attachments
    // debounce(() => {
    //   localStorage.setItem(
    //     `job.tasks_${job.id}`,
    //     JSON.stringify({
    //       ...savedJobTasks,
    //       [`task.form_${task.id}`]: taskFormValuesToSave,
    //     })
    //   );
    // }, 2000)();
  }

  const taskAssets = useTaskAssets(task);

  const [tagOptions] = useTags(TagEntityType.Task);

  // Add cleanup for localStorage when component unmounts
  useEffect(() => {
    return () => {
      // Optional: Clean up localStorage entries older than 24 hours to prevent bloat
      try {
        const savedJobTasks = localStorage.getItem(`job.tasks_${job.id}`);
        if (savedJobTasks) {
          const parsed = JSON.parse(savedJobTasks);
          // Could add timestamp-based cleanup logic here if needed
        }
      } catch (error) {
        console.warn('Failed to cleanup localStorage:', error);
      }
    };
  }, [job.id]); // Only depend on job.id since it's stable

  const closeAssetPopup = () => {
    setShowAsset(null);
  };

  const assetPopupTitle = () => {
    if (showAsset?.action === EDIT) {
      return t('editAsset');
    }
    if (showAsset?.action === VIEW) {
      return showAsset?.name;
    }
    if (showAsset?.action === DEPLOY) {
      return `Deploy ${showAsset?.name}`;
    }
    return t('translation:createAsset');
  };

  const { isMobile } = useBreakpoint();

  // Extract sharing permissions from task config for external users ONLY
  // Internal users (owners/admins) always have full access - no restrictions
  // External users see restricted content based on task config
  const sharingPermissions = useMemo(() => {
    // If user is internal (owner/admin), don't apply any restrictions
    if (shouldShowPermissions) return undefined;

    // If no config, don't apply restrictions (default to full access)
    if (!task.config) return undefined;

    // Apply sharing restrictions for external users
    const permissions = {
      shareAttributes: task.config.shareAttributes,
      shareJobHistory: task.config.shareJobHistory,
      shareDeploymentHistory: task.config.shareDeploymentHistory,
    };

    return permissions;
  }, [task.config, shouldShowPermissions]);

  const required = task.required;
  const showMandatoryTaskHelper =
    required &&
    task.status !== JobStatus.Complete &&
    task.status !== JobStatus.Incomplete;
  return (
    <>
      <PopupDialogNew
        isOpen={!!showAsset?.action}
        onClose={() => closeAssetPopup()}
        title={assetPopupTitle()}
      >
        {(() => {
          if (showAsset?.action === VIEW && showAsset.id) {
            // CRITICAL: Use task.job.id if available (correct job for this task), otherwise fallback to job.id from props
            // This ensures we pass the correct jobId that matches the task's actual job, not the job from URL params
            const taskJobId = task?.job?.id ?? job.id;

            // Warn if task.job.id exists but differs from job.id (indicates mismatched job assignment)
            if (task?.job?.id && task.job.id !== job.id) {
              console.warn(
                '[Custom.tsx TaskForm] ⚠️ WARNING: task.job.id differs from job.id prop!'
              );
            }

            return (
              <AssetView
                isPopup={true}
                id={showAsset.id}
                jobId={taskJobId}
                taskId={task.id}
                sharingPermissions={sharingPermissions}
              />
            );
          } else if (showAsset?.action === EDIT && showAsset.id) {
            return (
              <AssetForm
                isPopup={true}
                id={showAsset.id}
                onSuccess={() => closeAssetPopup()}
              />
            );
          } else if (showAsset?.action === DEPLOY) {
            return (
              <DeploymentForm
                asset={showAsset}
                providedAssetData={showAsset} // The asset data from task already has deployment info
                onClose={() => closeAssetPopup()}
                onSuccess={() => closeAssetPopup()}
                isPopup={true}
              />
            );
          } else if (showAsset?.action === CREATE) {
            return (
              <AssetForm
                isPopup={true}
                onSuccess={async (assetId) => {
                  if (!assetId) return;

                  await create({
                    input: {
                      assetId,
                      locationId: job.location?.id,
                      spaceName: '',
                      startDate: new Date(),
                    },
                  });
                  closeAssetPopup();
                }}
              />
            );
          } else {
            return null;
          }
        })()}
      </PopupDialogNew>
      <Form className='z-30 h-full w-full lg:w-auto'>
        <PromptEffect cancel='Stay with task' />
        <SideLayout willOverflow>
          <SideLayout.Head iconClass='arrow-left' onClose={onClose}>
            {task.name}
          </SideLayout.Head>
          <SideLayout.Body>
            {task.status !== JobStatus.Created && (
              <StatusBadge prefix={t('job:task')} value={task.status} />
            )}

            <div className='px-4'>
              {showMandatoryTaskHelper && (
                <MandatoryTaskHelper jobName={job.name} />
              )}

              {description && (
                <>
                  <div className='label mb-1.5'>{t('description')}</div>
                  <div className='mb-6'>{description}</div>
                </>
              )}
            </div>

            {task.attributes && task.attributes.length > 0 && (
              <AttributeView attributes={task.attributes} />
            )}

            {task.jobAttributeIds && task.jobAttributeIds.length > 0 && (
              <div className='px-4'>
                <CollectAttributes
                  entityType='Task'
                  entityId={task.id}
                  selections={task.jobAttributeIds}
                  values={task.taskAttributes}
                  readMode={task.status !== JobStatus.Created}
                />
              </div>
            )}
            {task.type === 'Asset' && (
              <>
                {' '}
                <div className='mb-2 mt-4 text-base font-medium text-primary'>
                  Selected Assets
                </div>
                <div className='rounded-lg border border-outline-bright bg-surface-bright py-1'>
                  {task.assets.length === 0 ? (
                    <div className='mx-3 my-4 text-center text-sm text-secondary'>
                      <div className='mb-2'>
                        ⚠️ No assets matched the selection criteria
                      </div>
                      {task.config?.selection === 'all' && (
                        <div className='text-xs text-tertiary'>
                          {task.config.spaces?.length > 0 && (
                            <div>Spaces: {task.config.spaces.join(', ')}</div>
                          )}
                          {task.config.assetTypes?.length > 0 && (
                            <div>
                              Asset Types: {task.config.assetTypes.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    taskAssets.map(
                      (asset: TaskQuery['task']['assets'][number] | string) => {
                        const isSeparator = typeof asset === 'string';

                        if (isSeparator) {
                          if (asset.startsWith('location:')) {
                            const locationName = asset.replace('location:', '');
                            return (
                              <div
                                key={asset}
                                className='mt-2 px-4 text-base font-medium text-primary'
                              >
                                <FontAwesomeIcon
                                  className='mr-2'
                                  icon={faLocationDot as IconProp}
                                />
                                {locationName}
                              </div>
                            );
                          } else if (asset.startsWith('space:')) {
                            const spaceName = asset.replace('space:', '');
                            return (
                              <div
                                key={asset}
                                className='mx-3 my-2 pl-1 text-sm text-secondary'
                              >
                                {spaceName}
                              </div>
                            );
                          }
                          return null;
                        }

                        return (
                          <div
                            key={asset.id}
                            className='mb-2 flex w-full cursor-pointer items-center'
                            role='button'
                            tabIndex={0}
                            aria-label={`View asset ${asset.name}`}
                            onClick={() =>
                              setShowAsset({
                                ...asset,
                                action: VIEW,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowAsset({
                                  ...asset,
                                  action: VIEW,
                                });
                              }
                            }}
                          >
                            <div className='flex-1'>
                              <CardSmall
                                linkTo=''
                                titleText={asset.name}
                                secondaryText={asset.type}
                                showImage
                                image={asset.image || asset.deployment?.image}
                              />
                            </div>
                            {canEditAssets && (
                              <div className='mb-2 mr-2 flex gap-1'>
                                <>
                                  <button
                                    type='button'
                                    onClick={(event) => {
                                      event?.stopPropagation();
                                      setShowAsset({
                                        ...asset,
                                        action: EDIT,
                                      });
                                    }}
                                    className='focus:ring-blue-500 -ml-1 flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white text-secondary hover:bg-gray-50 hover:text-primary focus:outline-none focus:ring-2'
                                    aria-label='Edit Asset'
                                    title='Edit Asset'
                                  >
                                    <PencilIcon className='h-4 w-4' />
                                  </button>

                                  {shouldShowDeployButton(asset) && (
                                    <button
                                      type='button'
                                      onClick={(event) => {
                                        event?.stopPropagation();
                                        setShowAsset({
                                          ...asset,
                                          action: DEPLOY,
                                        });
                                      }}
                                      className='focus:ring-blue-500 flex h-11 items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm text-secondary hover:bg-gray-50 hover:text-primary focus:outline-none focus:ring-2'
                                      aria-label='Deploy Asset'
                                      title='Deploy Asset'
                                    >
                                      <span>Deploy</span>
                                    </button>
                                  )}
                                </>
                              </div>
                            )}
                          </div>
                        );
                      }
                    )
                  )}
                </div>
              </>
            )}

            {/*          {task.type === 'Asset' && task?.config?.allow.includes(CREATE) && (
              <button
                type='button'
                onClick={() => setShowCreateNewAsset(!showCreateNewAsset)}
                className='mx-3 mb-5 mt-1 flex items-center gap-2 rounded-3xl border px-5 py-2 text-sm shadow-sm hover:text-brand hover:shadow'
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Create Asset</span>
              </button>
            )}
 */}
            {isEnabled && (
              <>
                <div className='mt-6 flex flex-col gap-[15px]'>
                  <div>
                    <UpdateTaskTagsField options={tagOptions} />
                  </div>

                  <div className='ml-1 text-base font-medium text-primary'>
                    Notes
                  </div>
                  <TextAreaField
                    name='notes'
                    placeholder='Add Task Notes...'
                    className='-mt-2.5'
                  />
                </div>

                {task.type === 'Standard' && (
                  <div className='text-center'>
                    <UploadField
                      id={task.id}
                      name='attachments'
                      attachments_v2={task.attachments ?? []}
                      onProgress={() => setUploading(true)}
                      onComplete={() => setUploading(false)}
                      multiple
                    />
                  </div>
                )}
                {task.type === 'Transfer Stock' && (
                  <TransferTaskField job={job} task={task} />
                )}
              </>
            )}
          </SideLayout.Body>
          <SideLayout.Foot className='p-4'>
            <WorkflowButtons
              id={task.id}
              jobStatus={job.status}
              taskStatus={task.status}
            >
              <CompleteTaskButton disabled={uploading} />
            </WorkflowButtons>
          </SideLayout.Foot>
        </SideLayout>
      </Form>
    </>
  );
};

export function MandatoryTaskHelper({ jobName }: { jobName: string }) {
  return (
    <div className='mb-3 flex items-center'>
      <HelperPopup
        icon={
          <div className='flex h-[18px] w-[18px] items-center justify-center rounded-full bg-orange'>
            <span className='text-[9px] font-bold text-white'>M</span>
          </div>
        }
      >
        You will need to complete or defer this task in order to complete{' '}
        {jobName}
      </HelperPopup>
      <span>Mandatory Task</span>
    </div>
  );
}

function CompleteTaskButton({ disabled }: { disabled?: boolean }) {
  const { t } = useTranslation('job');
  const { submitForm, setFieldValue } = useFormikContext();

  const handleClick = useCallback(() => {
    setFieldValue('date', new Date());
    submitForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Button type='button' disabled={disabled} onClick={handleClick}>
      {t('completeTask')}
    </Button>
  );
}
